import { assets } from "./assets";
import type { ProgramStat } from "./programs/types";

export const aboutHeroSlides = [
  {
    image: assets.hero,
    title: "Community Development",
    description: "Building stronger communities through collaborative efforts",
  },
  {
    image: "/assets/Educational-Support.webp",
    title: "Education Initiatives",
    description: "Empowering youth through quality education programs",
  },
  {
    image: "/assets/HealthCare-image.png",
    title: "Healthcare Services",
    description: "Providing essential healthcare to underserved communities",
  },
] as const;

export const aboutIntro = {
  headline: "About AJA Abhaya Hastham",
  lead: "Founded with a vision to create lasting positive change, AJA Abhaya Hastham has been at the forefront of community development for over a decade.",
  paragraphs: [
    "Our mission is to empower communities through education, provide access to quality healthcare, and promote sustainable development practices that benefit both current and future generations.",
  ],
};

export const aboutHero = {
  eyebrow: "About Us",
  title: "About",
  accentWord: "AJA Abhaya Hastham",
  subtitle:
    "Founded with a vision to create lasting positive change, AJA Abhaya Hastham has been at the forefront of community development for over a decade.",
  image: { src: assets.fieldWork[0], alt: "Community field work" },
  ctas: {
    primary: { href: "/donate", label: "Donate" },
    secondary: { href: "/contact", label: "Get involved" },
  },
};

export const aboutHeroStats = [
  { value: "4+", label: "Years of Service" },
  { value: "10+", label: "Projects Completed" },
  { value: "Section 8", label: "Non-Profit Company" },
  { value: "Hyderabad", label: "Telangana, India" },
] satisfies ProgramStat[];

export const aboutLeadership = {
  title: "Our Leadership",
  accentWord: "Leadership",
  subtitle:
    "Meet the dedicated individuals guiding our mission and driving positive change in communities",
};

export const aboutClosingCta = {
  title: "Become part of our story",
  description:
    "We're always looking for passionate individuals to help lead our initiatives and make a greater impact.",
  primary: { href: "/contact", label: "Volunteer Opportunities" },
  secondary: { href: "/contact", label: "Contact Our Team" },
};

export const aboutVision = {
  title: "Our Vision",
  description:
    "To create a world where every individual has access to education, healthcare, and opportunities to reach their full potential, creating thriving, self-sufficient communities.",
};

export const aboutMission = {
  title: "Our Mission",
  description:
    "To empower underprivileged communities through comprehensive education programs, provide access to quality healthcare, and promote sustainable development practices.",
};

export const aboutStoryOverview = {
  title: "Overview",
  paragraphs: [
    "In a nation as vast and diverse as India, where millions still struggle for basic necessities, the presence of a compassionate, purpose-driven organisation becomes a beacon of hope. Abhaya Hastham Foundation, formed as a Section 8 non-profit company, stands as a symbol of humanitarian service — a force of kindness dedicated to uplifting lives, restoring dignity, and building a future filled with opportunity.",
    'For countless individuals across communities, Abhaya Hastham represents more than an organisation. It embodies empathy in action, extending a protective hand ("Abhaya Hastham") to those who need it the most.',
  ],
} as const;

export const aboutStoryTimeline = [
  {
    step: "01",
    title: "A Vision That Lives Beyond Generations",
    description:
      "The journey of Abhaya Hastham began with a simple yet powerful belief: no one should be left unseen, unheard, or unsupported. What started as a small idea — to serve the underprivileged — has grown into a movement that touches lives through food support, healthcare assistance, blanket distribution, education help, and countless acts of compassion. The founders of the organisation, driven by purpose rather than profit, envisioned a platform where every resource is reinvested into people, communities, and their future. This spirit continues to guide the foundation as it expands its reach and impact.",
  },
  {
    step: "02",
    title: "A Legacy of Service",
    description:
      "India has a long-standing tradition of service — a belief that those who are fortunate carry a responsibility towards those in need. Abhaya Hastham Foundation brings this ancient value into modern times, creating structured, transparent, and impactful programs that address real challenges in society. From delivering essential supplies to families in crisis, to organising blood donation drives, to supporting homeless individuals with blankets and care — every initiative reflects the foundation's unwavering commitment to humanity. These efforts, woven over time, have created a growing legacy of hope, compassion, and empowerment.",
  },
  {
    step: "03",
    title: "Towards a Better Tomorrow",
    description:
      "Today, Abhaya Hastham Foundation continues to evolve, driven by a mission to create meaningful change across society. With every initiative, the organisation moves a step closer to its vision:",
  },
] as const;

export const aboutStoryQuote = {
  quote:
    "A nation where no one sleeps hungry, no child is denied education, no patient suffers alone, and every human being is treated with dignity.",
  closing:
    "This is not just the story of an organisation — it is a story of compassion, responsibility, and hope. A story that has only begun… with millions more lives waiting to be touched.",
} as const;

/** @deprecated Use aboutStoryOverview, aboutStoryTimeline, aboutStoryQuote */
export const aboutStorySections = [
  {
    title: aboutStoryOverview.title,
    content: [...aboutStoryOverview.paragraphs],
  },
  ...aboutStoryTimeline.map((item) => ({
    title: item.title,
    content: [item.description],
  })),
  {
    title: "Towards a Better Tomorrow",
    content: [aboutStoryTimeline[2].description],
    quote: aboutStoryQuote.quote,
  },
] as const;

export type AboutValueIcon = "heart" | "shield" | "handshake" | "target" | "leaf";

export const aboutValues = [
  { title: "Empathy", description: "Seeing every individual with humanity", icon: "heart" as AboutValueIcon },
  { title: "Integrity", description: "Maintaining transparency in all activities", icon: "shield" as AboutValueIcon },
  { title: "Service", description: "Putting community needs above everything", icon: "handshake" as AboutValueIcon },
  { title: "Commitment", description: "Ensuring every effort creates a real impact", icon: "target" as AboutValueIcon },
  { title: "Sustainability", description: "Building long-lasting solutions", icon: "leaf" as AboutValueIcon },
] as const;

export const aboutValuesFooter =
  "Each activity, each project, and each smile delivered stands as a testament to these principles.";

export const fieldPhotos = assets.fieldWork;
